import { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { API_BASE_URL } from '../config';

/**
 * SimplyLoan › Tracking: one page per market with yesterday's / the week's numbers.
 * Data: GET /api/simplyloan/tracking?geo=&period= (lambda_extract/simplyloan_tracking_service.py).
 */

type Kpi = { value: number; prev: number; change_pct: number | null };
type ChannelRow = { channel: string; leads: number; clicks: number; applications: number; paid: number; revenue_dkk: number };
type PartnerRow = {
  slug: string; name: string; network: string; clicks: number; leads_clicking: number; applications: number;
  paid: number; revenue_dkk: number; revenue_per_click: number | null; reports_applications: boolean;
};
type Alert = { level: 'critical' | 'warning'; title: string; detail: string };
type Consent = { leads: number; consented: number; rate_pct: number | null; prev_rate_pct: number | null; change_points: number | null; via_results_page: number; known: boolean };
type Exclusion = { id: number; kind: 'ip' | 'email' | 'lead_id'; value: string; note: string | null; created_by: string | null; created_at: string };
type NoBlock = {
  leads: number; salus_passed: number; leads_clicking: number; ctr_pct: number;
  applications: number; paid: number; revenue_dkk: number; epc_dkk: number;
};
type NoSegmentRow = NoBlock & { segment: string; label: string; share_pct: number };
type NoIncomeRow = NoBlock & { band: string; label: string; prime_leads: number };
type NoStep2Funnel = {
  leads_in_period: number; step1_leads: number; step2_completed: number; step2_rate_pct: number;
  prime_qualified: number; prime_rate_pct: number; whitelabel_routed: number; prime_fallback: number;
  restart_homeowner: number; clicked: number; applied: number; paid: number;
};
/** Norway only: the 2-step qualification funnel and unit economics per segment / income band (no_step2 in the API) */
type NoStep2 = {
  known: boolean; since: string; step2_funnel: NoStep2Funnel; segments: NoSegmentRow[];
  income_cohorts: NoIncomeRow[]; previous?: NoStep2Funnel | null;
};
type Tracking = {
  geo: string;
  period: { key: string; label: string; from: string; to: string };
  compare_label: string;
  kpis: { leads: Kpi; clicks: Kpi; applications: Kpi; paid: Kpi; revenue_dkk: Kpi };
  consent: Consent;
  excluded: { rules: number; leads_removed: number };
  channels: ChannelRow[];
  partners: PartnerRow[];
  funnel: { leads: number; salus_passed: number; clicked: number; applied: number; paid: number };
  no_step2?: NoStep2 | null;
  daily: { date: string; leads: number; clicks: number; revenue_dkk: number }[];
  alerts: Alert[];
  notes: string[];
  generated_at: string;
};

const MARKETS = [
  { id: 'se', label: 'Sweden' },
  { id: 'no', label: 'Norway' },
  { id: 'fi', label: 'Finland' },
];
const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: '7 days' },
  { id: '28d', label: '28 days' },
];
const STORAGE_KEY = 'sl_tracking_view';

const nf = new Intl.NumberFormat('da-DK');
const fmt = (n: number | null | undefined) => nf.format(n ?? 0);
const kr = (n: number | null | undefined) => `${fmt(Math.round(n ?? 0))} kr`;
const nf2 = new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kr2 = (n: number | null | undefined) => `${nf2.format(n ?? 0)} kr`;
const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–');
const shortDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()}/${d.getMonth() + 1}`;
};

function readView(): { geo: string; period: string } {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      geo: MARKETS.some((m) => m.id === saved.geo) ? saved.geo : 'se',
      period: PERIODS.some((p) => p.id === saved.period) ? saved.period : 'yesterday',
    };
  } catch {
    return { geo: 'se', period: 'yesterday' };
  }
}

function Segmented({ options, value, onChange }: { options: { id: string; label: string }[]; value: string; onChange: (id: string) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
            value === o.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function KpiTile({ label, kpi, money, compare }: { label: string; kpi: Kpi; money?: boolean; compare: string }) {
  const change = kpi.change_pct;
  const up = change !== null && change > 0;
  const down = change !== null && change < 0;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-3xl font-bold text-gray-900">{money ? kr(kpi.value) : fmt(kpi.value)}</div>
      <div className={`mt-1 text-xs font-medium ${up ? 'text-emerald-700' : down ? 'text-red-600' : 'text-gray-400'}`}>
        {change === null ? `no data ${compare}` : `${up ? '▲' : down ? '▼' : '•'} ${Math.abs(change)}% ${compare}`}
        <span className="text-gray-400 font-normal"> · was {money ? kr(kpi.prev) : fmt(kpi.prev)}</span>
      </div>
    </div>
  );
}

function ConsentTile({ consent, compare }: { consent: Consent; compare: string }) {
  const change = consent.change_points;
  const up = change !== null && change > 0;
  const down = change !== null && change < 0;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="text-sm font-medium text-gray-500">Marketing consent</div>
      <div className="mt-1 text-3xl font-bold text-gray-900">{consent.known && consent.rate_pct !== null ? `${Math.round(consent.rate_pct)}%` : '–'}</div>
      <div className="mt-1 text-xs text-gray-500">
        {consent.known ? (
          <>
            {fmt(consent.consented)} of {fmt(consent.leads)} leads said yes
            {consent.via_results_page > 0 && <span className="text-gray-400"> · {fmt(consent.via_results_page)} on the results page</span>}
          </>
        ) : (
          'lead database did not answer'
        )}
      </div>
      <div className={`mt-0.5 text-xs font-medium ${up ? 'text-emerald-700' : down ? 'text-red-600' : 'text-gray-400'}`}>
        {change === null ? `no data ${compare}` : `${up ? '▲' : down ? '▼' : '•'} ${Math.abs(change)} points ${compare}`}
      </div>
    </div>
  );
}

const KIND_LABEL: Record<Exclusion['kind'], string> = { ip: 'IP address', email: 'E-mail', lead_id: 'Lead id' };

function ExclusionsPanel({ onChange }: { onChange: () => void }) {
  const [rows, setRows] = useState<Exclusion[]>([]);
  const [yourIp, setYourIp] = useState('');
  const [kind, setKind] = useState<Exclusion['kind']>('ip');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = `${API_BASE_URL}/api/simplyloan/tracking/exclusions`;

  const apply = useCallback(async (init?: RequestInit, id?: number) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(id ? `${url}?id=${id}` : url, init);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setRows(json.rows || []);
      setYourIp(json.your_ip || '');
      if (init) onChange();
    } catch (e: any) {
      setError(e?.message || 'Could not update the list');
    } finally {
      setBusy(false);
    }
  }, [url, onChange]);

  useEffect(() => {
    apply();
  }, [apply]);

  const add = async () => {
    if (!value.trim()) return;
    await apply({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, value, note }) });
    setValue('');
    setNote('');
  };
  const thisDevice = () => apply({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ this_device: true, note: 'this device' }) });
  const remove = (id: number) => apply({ method: 'DELETE' }, id);
  const myIpListed = yourIp !== '' && rows.some((r) => r.kind === 'ip' && r.value === yourIp);

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-800">Our own activity</h2>
          <p className="text-xs text-gray-500 mt-0.5">Leads, clicks, applications and revenue from these are left out of every number above. Test leads (agtest, example.com) are always left out.</p>
        </div>
        <button
          type="button"
          onClick={thisDevice}
          disabled={busy || myIpListed || !yourIp}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          title={yourIp ? `Your IP address is ${yourIp}` : ''}
        >
          {myIpListed ? `This device is excluded (${yourIp})` : `Exclude this device${yourIp ? ` (${yourIp})` : ''}`}
        </button>
      </div>

      {error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</div>}

      <ul className="mt-3 divide-y divide-gray-100 text-sm">
        {rows.length === 0 && <li className="py-2 text-gray-400">Nothing excluded yet.</li>}
        {rows.map((r) => (
          <li key={r.id} className="py-2 flex items-center gap-3">
            <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">{KIND_LABEL[r.kind]}</span>
            <span className="font-medium text-gray-900 tabular-nums">{r.value}</span>
            {r.note && <span className="text-gray-500 truncate">{r.note}</span>}
            <button type="button" onClick={() => remove(r.id)} disabled={busy} className="ml-auto text-xs text-gray-400 hover:text-red-600" aria-label={`Remove ${r.value}`}>
              Remove
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as Exclusion['kind'])} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
          <option value="ip">IP address</option>
          <option value="email">E-mail or @domain</option>
          <option value="lead_id">Lead id</option>
        </select>
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={kind === 'ip' ? '89.23.224.62' : kind === 'email' ? 'name@mail.com or @afima.dk' : '107184'} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm w-56" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm w-48" />
        <button type="button" onClick={add} disabled={busy || !value.trim()} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          Add
        </button>
      </div>
    </section>
  );
}

function Alerts({ alerts }: { alerts: Alert[] }) {
  if (!alerts.length) {
    return (
      <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        Nothing needs attention.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {alerts.map((a, i) => (
        <li
          key={i}
          className={`rounded-xl border px-4 py-3 text-sm flex gap-3 ${
            a.level === 'critical' ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          {a.level === 'critical' ? <XCircle size={18} className="shrink-0 mt-0.5 text-red-600" /> : <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-600" />}
          <div>
            <div className="font-semibold">
              {a.level === 'critical' ? 'Critical: ' : 'Check: '}
              {a.title}
            </div>
            <div className="text-xs opacity-80 mt-0.5">{a.detail}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

const th = 'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50';
const td = 'px-4 py-2 text-sm text-gray-700 whitespace-nowrap';
const num = (n: number | null | undefined, render: (v: number) => string = fmt) =>
  n ? <span className="tabular-nums">{render(n)}</span> : <span className="text-gray-300">0</span>;

function Funnel({ funnel }: { funnel: Tracking['funnel'] }) {
  const steps = [
    { label: 'Leads (form sent)', value: funnel.leads },
    { label: 'Approved by Salus', value: funnel.salus_passed },
    { label: 'Clicked a partner', value: funnel.clicked },
    { label: 'Applied', value: funnel.applied },
    { label: 'Paid', value: funnel.paid },
  ];
  return (
    <div className="space-y-2.5">
      {steps.map((s, i) => (
        <div key={s.label} className="grid grid-cols-[150px_1fr_90px] items-center gap-3 text-sm">
          <span className="text-gray-600">{s.label}</span>
          <div className="h-3.5 rounded-r bg-indigo-50">
            <div className="h-full rounded-r bg-indigo-500" style={{ width: funnel.leads ? `${Math.max(1, (s.value / funnel.leads) * 100)}%` : '0%' }} />
          </div>
          <span className="text-right tabular-nums font-semibold text-gray-900">
            {fmt(s.value)} <span className="text-xs font-normal text-gray-400">{i === 0 ? '' : pct(s.value, steps[i - 1].value)}</span>
          </span>
        </div>
      ))}
      <p className="text-xs text-gray-400 pt-1">Website leads only. Percentages are of the previous step. Applications and payouts can arrive days after the lead.</p>
    </div>
  );
}

function NoStep2FunnelCard({ block, compare }: { block: NoStep2; compare: string }) {
  const f = block.step2_funnel;
  const prev = block.previous;
  const width = (v: number) => (f.step1_leads ? `${Math.max(1, (v / f.step1_leads) * 100)}%` : '0%');
  const row = (label: string, value: number, of: number | null, ofLabel: string) => (
    <div key={label} className="grid grid-cols-[150px_1fr_110px] items-center gap-3 text-sm">
      <span className="text-gray-600">{label}</span>
      <div className="h-3.5 rounded-r bg-indigo-50">
        <div className="h-full rounded-r bg-indigo-500" style={{ width: width(value) }} />
      </div>
      <span className="text-right tabular-nums font-semibold text-gray-900">
        {fmt(value)}{' '}
        {of !== null && <span className="text-xs font-normal text-gray-400">{pct(value, of)} {ofLabel}</span>}
      </span>
    </div>
  );
  const left = f.leads_in_period - f.step1_leads;
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold text-gray-800">Norway 2-step qualification funnel</h2>
        <span className="text-xs text-gray-400">since {block.since.slice(0, 16).replace('T', ' ')}</span>
      </div>
      {!block.known && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          The lead database did not answer, so the Step 2 fields are unknown for this period.
        </div>
      )}
      <div className="space-y-2.5">
        {row('Step 1 leads', f.step1_leads, null, '')}
        {row('Step 2 completed', f.step2_completed, f.step1_leads, 'of step 1')}
        {row('Prime qualified', f.prime_qualified, f.step2_completed, 'of step 2')}
        <div className="grid grid-cols-2 gap-3 py-1">
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2">
            <div className="text-xs text-gray-500">DigiFinans whitelabel</div>
            <div className="text-lg font-semibold text-gray-900 tabular-nums">
              {fmt(f.whitelabel_routed)} <span className="text-xs font-normal text-gray-400">{pct(f.whitelabel_routed, f.prime_qualified)} of prime</span>
            </div>
          </div>
          <div className="rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2">
            <div className="text-xs text-gray-500">Prime fallback (Salus said no)</div>
            <div className="text-lg font-semibold text-gray-900 tabular-nums">
              {fmt(f.prime_fallback)} <span className="text-xs font-normal text-gray-400">{pct(f.prime_fallback, f.prime_qualified)} of prime</span>
            </div>
          </div>
        </div>
        {row('Partner clicks', f.clicked, f.step2_completed, 'of step 2')}
        {row('Paid', f.paid, f.clicked, 'of clicks')}
      </div>
      <p className="text-xs text-gray-400 pt-3">
        {fmt(f.restart_homeowner)} omstartslån leads (payment remarks + homeowner) went to the comparison page.
        {prev ? ` ${compare}: ${fmt(prev.step1_leads)} leads, ${prev.step2_rate_pct}% completed step 2, ${fmt(prev.prime_qualified)} prime.` : ''}
        {left > 0 ? ` ${fmt(left)} leads from before the 2-step launch are left out of the funnel.` : ''}
      </p>
    </section>
  );
}

function NoClicks({ row }: { row: NoBlock }) {
  if (!row.leads_clicking) return <span className="text-gray-300">0</span>;
  return (
    <span className="tabular-nums">
      {fmt(row.leads_clicking)} <span className="text-xs text-gray-400">({row.ctr_pct}%)</span>
    </span>
  );
}

function NoEpc({ row }: { row: NoBlock }) {
  return row.epc_dkk ? <span className="tabular-nums">{kr2(row.epc_dkk)}</span> : <span className="text-gray-300">–</span>;
}

function NoSegmentsTable({ rows }: { rows: NoSegmentRow[] }) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <h2 className="text-sm font-semibold text-gray-800 px-5 pt-4 pb-2">Unit economics by qualification segment (NO)</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr>
              <th className={`${th} text-left`}>Segment</th>
              <th className={`${th} text-right`}>Leads</th>
              <th className={`${th} text-right`}>Share</th>
              <th className={`${th} text-right`}>Whitelabel (Salus)</th>
              <th className={`${th} text-right`}>Partner clicks (CTR)</th>
              <th className={`${th} text-right`}>Apps</th>
              <th className={`${th} text-right`}>Paid</th>
              <th className={`${th} text-right`}>Revenue</th>
              <th className={`${th} text-right`}>EPC (kr/lead)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr><td className={`${td} text-gray-400`} colSpan={9}>No leads in this period.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.segment} className={r.leads ? '' : 'text-gray-400'}>
                <td className={`${td} font-medium text-gray-900`}>{r.label}</td>
                <td className={`${td} text-right`}>{num(r.leads)}</td>
                <td className={`${td} text-right`}>{r.leads ? <span className="tabular-nums">{r.share_pct}%</span> : <span className="text-gray-300">–</span>}</td>
                <td className={`${td} text-right`}>{num(r.salus_passed)}</td>
                <td className={`${td} text-right`}><NoClicks row={r} /></td>
                <td className={`${td} text-right`}>{num(r.applications)}</td>
                <td className={`${td} text-right`}>{num(r.paid)}</td>
                <td className={`${td} text-right`}>{num(r.revenue_dkk, kr)}</td>
                <td className={`${td} text-right font-semibold text-gray-900`}><NoEpc row={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-5 pb-4 pt-2 text-xs text-gray-400">
        Segments come from Step 2 (age, income, employment, payment remarks, home ownership). Whitelabel = prime leads DigiFinans accepted directly.
        EPC is the revenue reported so far divided by the leads in the segment; payouts can arrive weeks after the click.
      </p>
    </section>
  );
}

function NoIncomeTable({ rows }: { rows: NoIncomeRow[] }) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <h2 className="text-sm font-semibold text-gray-800 px-5 pt-4 pb-2">Performance by monthly income (NOK)</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr>
              <th className={`${th} text-left`}>Income / month</th>
              <th className={`${th} text-right`}>Leads</th>
              <th className={`${th} text-right`}>Prime</th>
              <th className={`${th} text-right`}>Whitelabel</th>
              <th className={`${th} text-right`}>Clicks (CTR)</th>
              <th className={`${th} text-right`}>Apps</th>
              <th className={`${th} text-right`}>Paid</th>
              <th className={`${th} text-right`}>Revenue</th>
              <th className={`${th} text-right`}>EPC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.band} className={r.leads ? '' : 'text-gray-400'}>
                <td className={`${td} font-medium text-gray-900`}>{r.label}</td>
                <td className={`${td} text-right`}>{num(r.leads)}</td>
                <td className={`${td} text-right`}>{num(r.prime_leads)}</td>
                <td className={`${td} text-right`}>{num(r.salus_passed)}</td>
                <td className={`${td} text-right`}><NoClicks row={r} /></td>
                <td className={`${td} text-right`}>{num(r.applications)}</td>
                <td className={`${td} text-right`}>{num(r.paid)}</td>
                <td className={`${td} text-right`}>{num(r.revenue_dkk, kr)}</td>
                <td className={`${td} text-right font-semibold text-gray-900`}><NoEpc row={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-5 pb-4 pt-2 text-xs text-gray-400">Step 2 leads only (income given before tax). Leads that stopped after Step 1 are not in these bands.</p>
    </section>
  );
}

export default function SimplyloanRedirectTrackingPage() {
  const [view, setView] = useState(readView);
  const [data, setData] = useState<Tracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/simplyloan/tracking?geo=${view.geo}&period=${view.period}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e: any) {
      setError(e?.message || 'Could not load');
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (patch: Partial<typeof view>) => {
    const next = { ...view, ...patch };
    setView(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* per-viewer convenience only */
    }
  };

  const showChart = view.period === '7d' || view.period === '28d';

  return (
    <MainLayout>
      <div className="p-6 space-y-6 max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SimplyLoan Tracking</h1>
            <p className="text-sm text-gray-500 mt-0.5">Leads, partner clicks, applications and revenue per market. Test traffic removed.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Segmented options={MARKETS} value={view.geo} onChange={(geo) => update({ geo })} />
            <Segmented options={PERIODS} value={view.period} onChange={(period) => update({ period })} />
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Could not load the numbers ({error}). Try again.</div>}

        {data && (
          <div className={`space-y-6 transition-opacity ${loading ? 'opacity-50' : ''}`}>
            <div className="text-xs text-gray-400">
              {data.period.label}: {data.period.from.slice(0, 16).replace('T', ' ')} – {data.period.to.slice(0, 16).replace('T', ' ')} · updated {data.generated_at.slice(11, 16)}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <KpiTile label="Leads" kpi={data.kpis.leads} compare={data.compare_label} />
              <ConsentTile consent={data.consent} compare={data.compare_label} />
              <KpiTile label="Partner clicks" kpi={data.kpis.clicks} compare={data.compare_label} />
              <KpiTile label="Applications" kpi={data.kpis.applications} compare={data.compare_label} />
              <KpiTile label="Paid deals" kpi={data.kpis.paid} compare={data.compare_label} />
              <KpiTile label="Revenue" kpi={data.kpis.revenue_dkk} money compare={data.compare_label} />
            </div>

            <section>
              <h2 className="text-sm font-semibold text-gray-800 mb-2">Needs attention</h2>
              <Alerts alerts={data.alerts} />
            </section>

            {showChart && (
              <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">Per day</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.daily} barGap={2} barCategoryGap="30%">
                    <CartesianGrid vertical={false} stroke="#eef0f3" />
                    <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={{ stroke: '#d1d5db' }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={36} />
                    <Tooltip labelFormatter={(v) => shortDate(String(v))} formatter={(v: any, name: any) => [fmt(Number(v)), name]} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="leads" name="Leads" fill="#2a78d6" radius={[3, 3, 0, 0]} maxBarSize={22} />
                    <Bar dataKey="clicks" name="Partner clicks" fill="#eb6834" radius={[3, 3, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </section>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <h2 className="text-sm font-semibold text-gray-800 px-5 pt-4 pb-2">Where it comes from</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        <th className={`${th} text-left`}>Source</th>
                        <th className={`${th} text-right`}>Leads</th>
                        <th className={`${th} text-right`}>Clicks</th>
                        <th className={`${th} text-right`}>Applications</th>
                        <th className={`${th} text-right`}>Paid</th>
                        <th className={`${th} text-right`}>Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.channels.length === 0 && (
                        <tr><td className={`${td} text-gray-400`} colSpan={6}>No traffic in this period.</td></tr>
                      )}
                      {data.channels.map((c) => (
                        <tr key={c.channel}>
                          <td className={`${td} font-medium text-gray-900`}>{c.channel}</td>
                          <td className={`${td} text-right`}>{num(c.leads)}</td>
                          <td className={`${td} text-right`}>{num(c.clicks)}</td>
                          <td className={`${td} text-right`}>{num(c.applications)}</td>
                          <td className={`${td} text-right`}>{num(c.paid)}</td>
                          <td className={`${td} text-right`}>{num(c.revenue_dkk, kr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">Lead funnel</h2>
                <Funnel funnel={data.funnel} />
              </section>
            </div>

            {data.geo === 'no' && data.no_step2 && (
              <>
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6">
                  <NoStep2FunnelCard block={data.no_step2} compare={data.compare_label} />
                  <NoIncomeTable rows={data.no_step2.income_cohorts} />
                </div>
                <NoSegmentsTable rows={data.no_step2.segments} />
              </>
            )}

            <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <h2 className="text-sm font-semibold text-gray-800 px-5 pt-4 pb-2">Partners</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className={`${th} text-left`}>Partner</th>
                      <th className={`${th} text-left`}>Network</th>
                      <th className={`${th} text-right`}>Clicks</th>
                      <th className={`${th} text-right`}>Leads clicking</th>
                      <th className={`${th} text-right`}>Applications</th>
                      <th className={`${th} text-right`}>Paid</th>
                      <th className={`${th} text-right`}>Revenue</th>
                      <th className={`${th} text-right`}>Per click</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.partners.length === 0 && (
                      <tr><td className={`${td} text-gray-400`} colSpan={8}>No partner clicks in this period.</td></tr>
                    )}
                    {data.partners.map((p) => (
                      <tr key={p.slug || p.name} className={p.slug ? '' : 'text-gray-400'}>
                        <td className={`${td} font-medium text-gray-900`}>{p.name}</td>
                        <td className={`${td} text-gray-500`}>{p.network || '–'}</td>
                        <td className={`${td} text-right`}>{num(p.clicks)}</td>
                        <td className={`${td} text-right`}>{num(p.leads_clicking)}</td>
                        <td className={`${td} text-right`} title={p.reports_applications ? '' : 'This network does not report applications'}>
                          {p.reports_applications ? num(p.applications) : <span className="text-gray-300">n/a</span>}
                        </td>
                        <td className={`${td} text-right`}>{num(p.paid)}</td>
                        <td className={`${td} text-right`}>{num(p.revenue_dkk, kr)}</td>
                        <td className={`${td} text-right`}>{p.revenue_per_click ? <span className="tabular-nums">{kr(p.revenue_per_click)}</span> : <span className="text-gray-300">–</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <ExclusionsPanel onChange={load} />

            <ul className="text-xs text-gray-400 space-y-1 list-disc pl-5">
              {data.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
              {data.excluded.leads_removed > 0 && <li>{fmt(data.excluded.leads_removed)} of our own leads were removed from this period.</li>}
            </ul>
          </div>
        )}

        {!data && loading && (
          <div className="flex items-center justify-center p-12 h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-indigo-600 font-medium tracking-wide">Loading…</span>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
